<template>
    <div class="pokemon-card-badges" :class="{'pokemon-card-badges--mirrored': rightHanded}">
        <TypePill v-if="pokemon && pokemon.pokemonType" class="pokemon-card-badges__pill" :type="pokemon.pokemonType.type" size="small">
            {{ $t('common.typeNames.' + pokemon.pokemonType.type.name).toUpperCase() }}
        </TypePill>
        <UseItemButton
            v-if="showItemButton && pokemon"
            class="pokemon-card-badges__pill"
            :pokemon="pokemon"
            :items="items"
            @used="$emit('item-used')"
            @use-error="$emit('item-use-error', $event)"
        />
        <v-btn
            v-if="pokemon && pokemon.endangered"
            rounded
            depressed
            x-small
            class="pokemon-card-badges__pill chip-dekorator-light-small align-center align-self-center grey lighten-2 font-weight-bold red--text"
            @click="showEndangeredInfo = true"
        >{{ $t('pokemon.common.endangeredLabel') }}</v-btn>
        <v-btn
            v-if="showOakToggle"
            small
            rounded
            class="pokemon-card-badges__pill"
            :class="oakState ? 'skin-btn-team' : 'skin-btn-box'"
            :loading="oakLoading"
            :disabled="disableOakToggle"
            @click="$emit('toggle-oak')"
        >{{ oakState ? $t('pokemon.pokemonManager.toggleOakYou') : $t('pokemon.pokemonManager.toggleOakOak') }}</v-btn>
        <!-- Evolve orb - presna kopie puvodni logiky z PokemonManager.vue/PokemonDetail.vue
             (vcetne eevee-stone filtru na cislech 133/1052/1098 pres dialogStones prop) -
             jen presunuto sem, viz spec "mimo rozsah: EeveeStonesDialog extrakce". -->
        <v-hover
            v-for="evolve in evolvableList"
            :key="evolve.id"
            v-slot="{ hover }"
            open-delay="200"
        >
            <div v-if="(evolve !== 133 && evolve !== 1052 && evolve !== 1098) || dialogStones && dialogStones.length > 0">
                <v-card
                    v-if="!inFight"
                    :elevation="hover ? 16 : 2"
                    class="pokemon-card-badges__evolve-card"
                    :class="{ 'on-hover': hover }"
                    @click="$emit('evolve', evolve)"
                >
                    <v-avatar size="64">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <div class="pokemon-card-badges__evolve-orb" v-bind="attrs" v-on="on">
                                    <v-img :src="evolveImgSrc(evolve.img)" :alt="evolve.name" size="60" contain/>
                                </div>
                            </template>
                            <span style="width: 80px;">{{ $t('pokemon.common.evolveTooltip') }}</span>
                        </v-tooltip>
                    </v-avatar>
                </v-card>
                <v-card
                    v-else
                    :elevation="hover ? 16 : 2"
                    class="pokemon-card-badges__evolve-card"
                    :class="{ 'on-hover': hover }"
                >
                    <v-avatar size="64">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <div class="pokemon-card-badges__evolve-orb pokemon-card-badges__evolve-orb--disabled grey" v-bind="attrs" v-on="on">
                                    <v-img :src="evolveImgSrc(evolve.img)" :alt="evolve.name" size="60" contain class="pokemon-card-badges__evolve-img--grayscale"/>
                                </div>
                            </template>
                            <span style="width: 80px;">{{ $t('pokemon.pokemonManager.cantEvolveInFightTooltip') }}</span>
                        </v-tooltip>
                    </v-avatar>
                </v-card>
            </div>
        </v-hover>
        <EndangeredInfoPopup v-model="showEndangeredInfo"/>
    </div>
</template>

<script>
    import {flags} from '@/config/featureFlags';
    import {pokemonImageSrc} from '@/utils/pokemonImage';
    import TypePill from "@/components/utils/TypePill";
    import UseItemButton from '@/components/pokemon/UseItemButton';
    import EndangeredInfoPopup from '@/components/pokemon/EndangeredInfoPopup';

    export default {
        name: 'PokemonCardBadges',
        components: {TypePill, UseItemButton, EndangeredInfoPopup},
        props: {
            pokemon: {type: Object, default: null},
            items: {type: Array, default: () => []},
            showItemButton: {type: Boolean, default: true},
            showOakToggle: {type: Boolean, default: false},
            oakState: {type: Boolean, default: false},
            oakLoading: {type: Boolean, default: false},
            disableOakToggle: {type: Boolean, default: false},
            inFight: {type: Boolean, default: false},
            // Host-managed stav pro eevee-stone evolve flow (viz PokemonManager.vue/
            // PokemonDetail.vue evolvePokemon()) - komponenta jen replikuje puvodni
            // v-if filtr, dialog samotny zustava v hostu.
            dialogStones: {type: Array, default: () => []},
        },
        data() {
            return {
                showEndangeredInfo: false,
            }
        },
        computed: {
            rightHanded() {
                return flags.rightHanded;
            },
            evolvableList() {
                return (this.pokemon && this.pokemon.evolvable) || [];
            },
        },
        methods: {
            evolveImgSrc(img) {
                return pokemonImageSrc(img);
            },
        },
    }
</script>

<style scoped>
.pokemon-card-badges {
    position: absolute;
    top: var(--pcb-top, 62px);
    left: var(--pcb-inset, 32px);
    z-index: 9;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
}
.pokemon-card-badges--mirrored {
    left: auto;
    right: var(--pcb-inset, 32px);
    align-items: flex-end;
}
/* Zoom hook pro host komponenty s container-query skalovanim (viz
   PokemonDetail.vue --pd-typepill-scale) - PokemonManager.vue (bez
   container-query systemu) necha na defaultni hodnote 1. */
.pokemon-card-badges__pill {
    zoom: var(--pcb-badge-scale, 1);
}
.pokemon-card-badges__evolve-card {
    width: 64px;
    height: 64px;
    border-radius: 50% 50% 50% 50%;
    /* Samostatny hook oddeleny od --pcb-badge-scale - stejny duvod jako
       --pd-evolve-scale vs. --pd-typepill-scale v PokemonDetail.vue (evolve
       koule potrebuje jinou skalu nez typovy pilulky/item tlacitko). */
    zoom: var(--pcb-evolve-scale, 1);
}
.pokemon-card-badges__evolve-orb {
    width: 64px;
    height: 64px;
    background: radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%), radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%);
    border: 2px black;
    border-radius: 50% 50% 50% 50%;
}
.pokemon-card-badges__evolve-orb--disabled {
    background: none;
}
.pokemon-card-badges__evolve-img--grayscale {
    -webkit-filter: grayscale(100%);
    filter: grayscale(100%);
}
</style>
